export { listCommandPrompt } from "./rules/lists.js";
// Shopping commands never invoke nutrition estimation or modify the food diary.
const text = (value, max = 200) => typeof value === "string" && value.length <= max;
const id = value => text(value, 100) && value.length > 0;
export function validListCommand(value) {
  if (!value || typeof value !== "object" || !(value.listId === null || id(value.listId)) || !(value.newListName === null || (text(value.newListName, 100) && value.newListName.trim())) || !text(value.question, 500) || !Array.isArray(value.operations) || value.operations.length > 100) return false;
  return value.operations.every(op => {
    if (!op || typeof op !== "object") return false;
    if (op.type === "addDish") return id(op.dishId);
    if (op.type === "delete") return id(op.itemId);
    if (op.type === "status") return id(op.itemId) && ["needed", "done", "missing"].includes(op.status);
    if (!["add", "update", "merge"].includes(op.type) || (op.type === "update" && !id(op.itemId))) return false;
    if (op.type === "merge" && (!Array.isArray(op.itemIds) || op.itemIds.length < 2 || op.itemIds.length > 100 || !op.itemIds.every(id) || new Set(op.itemIds).size !== op.itemIds.length)) return false;
    const item = op.item;
    return item && text(item.name) && item.name.trim().length > 0 && (item.amount === null || (typeof item.amount === "number" && Number.isFinite(item.amount) && item.amount > 0 && item.amount <= 1_000_000)) && text(item.unit, 40) && text(item.note, 500) && text(item.group, 100) && (op.type === "merge" ? ["set", "sum"] : ["set", "increment"]).includes(op.amountMode);
  });
}

export function enumeratedListItems(value) {
  if (typeof value !== "string" || /(?:^|\s)(?:удали|убери|измени|замени|переименуй|объедини)(?:\s|$)/iu.test(value)) return [];
  const marker = /(?:^|[,.!?;:]|\s)(?:перв(?:ое|ый|ая)|втор(?:ое|ой|ая)|треть(?:е|ий|я)|четв[её]рт(?:ое|ый|ая)|пят(?:ое|ый|ая)|шест(?:ое|ой|ая)|седьм(?:ое|ой|ая)|восьм(?:ое|ой|ая)|девят(?:ое|ый|ая)|десят(?:ое|ый|ая))(?:\s*[-—:]|\s+)/giu;
  const matches = [...value.matchAll(marker)];
  if (matches.length < 2) return [];
  return matches.map((match, index) => value.slice((match.index ?? 0) + match[0].length, matches[index + 1]?.index ?? value.length).trim()
    .replace(/^(?:надо\s+)?(?:купить|добавить|положить)\s+/iu, "")
    .replace(/(?:\s+и)?\s*(?:это\s+вс[её]|на\s+этом\s+вс[её]|у\s+меня\s+вс[её]|(?:я\s+)?закончил(?:а)?|вс[её]\s+готово|готово|(?:вс[её]\s*,?\s*)?больше\s+ничего)[.!?]*$/iu, "")
    .replace(/\s+и$/iu, "")
    .replace(/^[\s,;:—-]+|[\s,;:.!?—-]+$/gu, "").trim())
    .filter(Boolean);
}

export function enumeratedListCommand(value, current) {
  const items = enumeratedListItems(value);
  if (items.length < 2 || !current || typeof current.id !== "string" || !current.id) return null;
  return {
    listId: current.id,
    newListName: null,
    question: "",
    operations: items.map(name => ({ type: "add", item: { name, amount: null, unit: "", note: "", group: "" }, amountMode: "set" })),
  };
}

const spokenCounts = new Map([["один", 1], ["одна", 1], ["одно", 1], ["два", 2], ["две", 2], ["три", 3], ["четыре", 4], ["пять", 5], ["шесть", 6], ["семь", 7], ["восемь", 8], ["девять", 9], ["десять", 10]]);
const explicitAdd = /^(?:добавь|добавить|допиши|внеси|запиши)(?:\s+ещ[её])?\s+/iu;
const otherAction = /(?:^|\s)(?:удали|убери|измени|замени|переименуй|объедини|отметь|поставь)(?:\s|$)/iu;

function directItem(value) {
  let source = value.trim().replace(/[.!?]+$/gu, "").trim();
  const add = explicitAdd.test(source);
  source = source.replace(explicitAdd, "").trim();
  if (!source || otherAction.test(source) || /[,;]|\s+(?:и|а)\s+/iu.test(source)) return null;
  const words = source.split(/\s+/u);
  const numeric = words[0]?.match(/^\d+(?:[.,]\d+)?$/u);
  const spoken = spokenCounts.get(words[0]?.toLocaleLowerCase("ru-RU"));
  const amount = numeric ? Number(numeric[0].replace(",", ".")) : spoken ?? null;
  if (amount !== null) words.shift();
  const name = words.join(" ").trim();
  if (!name) return null;
  return { add, item: { name, amount, unit: amount === null ? "" : "шт.", note: "", group: "" } };
}

export function directListCommand(value, current, currentItem = null) {
  if (typeof value !== "string" || !current || typeof current.id !== "string" || !current.id) return null;
  if (/^(?:отмена|отмени|отменить|не\s+записывай|не\s+записывать)[.!?,\s]*$/iu.test(value.trim())) return { listId: current.id, newListName: null, question: "", operations: [] };
  const parsed = directItem(value);
  if (!parsed || (!parsed.add && !currentItem?.id)) return null;
  return { listId: current.id, newListName: null, question: "", operations: [parsed.add
    ? { type: "add", item: parsed.item, amountMode: "set" }
    : { type: "update", itemId: currentItem.id, item: parsed.item, amountMode: "set" }] };
}
